import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { IconArrowLeft } from '@shared/ui/icons';
import { Button } from '@shared/ui/Button';
import { Input, Label, Field } from '@shared/ui/Input';
import { useAgentPermission } from '@entities/user';
import type { LogistSigner } from '@entities/logistics';
import {
  apiErrorText,
  useListLogistSignersQuery,
  useListLogistSignerStatusesQuery,
  useUpsertLogistSignerMutation,
  useSetLogistSignerActiveMutation,
} from '@shared/api';

/**
 * ОСНОВА.Логист — подписанты титулов ЭТрН (ТЗ AI-LOGIST-2026 §4.2).
 *
 * Формат ФНС требует в накладной ФИО, должность и основание полномочий
 * подписанта, а для доверенности — её реквизиты. Карточка заводится на
 * сотрудника: выпуск берёт карточку того, кто выпускает документ.
 *
 * Подпись КЭП остаётся человеком в кабинете оператора — здесь только сведения,
 * которые уходят в сам документ.
 */

const Wrap = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 24px 20px 64px;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  text-decoration: none;
  margin-bottom: 16px;
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Title = styled.h1`
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 650;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  line-height: 1.5;
`;

const Card = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 11px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const Note = styled.p<{ $kind: 'ok' | 'err' | 'muted' }>`
  margin: 12px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme, $kind }) =>
    $kind === 'ok'
      ? theme.colors.status.success
      : $kind === 'err'
        ? theme.colors.status.danger
        : theme.colors.text.secondary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.primary};
  vertical-align: top;
`;

const Badge = styled.span<{ $tone: 'ok' | 'warn' | 'off' }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: ${({ theme, $tone }) =>
    $tone === 'ok'
      ? `${theme.colors.status.success}22`
      : $tone === 'warn'
        ? `${theme.colors.status.warning}22`
        : `${theme.colors.text.secondary}22`};
  color: ${({ theme, $tone }) =>
    $tone === 'ok'
      ? theme.colors.status.success
      : $tone === 'warn'
        ? theme.colors.status.warning
        : theme.colors.text.secondary};
`;

const EMPTY = {
  full_name: '',
  job_title: '',
  status: '',
  poa_id: '',
  poa_date: '',
  poa_number: '',
  poa_valid_until: '',
  notes: '',
};

/** Просрочена ли доверенность на сегодня (сравнение по дате, без времени). */
function isExpired(signer: LogistSigner): boolean {
  if (!signer.poa_valid_until) return false;
  return signer.poa_valid_until < new Date().toISOString().slice(0, 10);
}

export function LogistSigners() {
  const { can_write: canWrite } = useAgentPermission('logist');
  const { data, isLoading } = useListLogistSignersQuery({ active: false });
  const { data: statusesData } = useListLogistSignerStatusesQuery();
  const [upsert, { isLoading: saving }] = useUpsertLogistSignerMutation();
  const [setActive] = useSetLogistSignerActiveMutation();

  const [form, setForm] = useState<Record<string, string>>(EMPTY);
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const statuses = useMemo(() => statusesData?.items ?? [], [statusesData]);
  const signers = data?.items ?? [];

  // Основание по умолчанию — первое из классификатора, чтобы поле не было пустым.
  useEffect(() => {
    if (!form.status && statuses.length > 0) {
      setForm((f) => ({ ...f, status: statuses[0].code }));
    }
  }, [statuses, form.status]);

  const needsPoa = useMemo(
    () => statuses.find((s) => s.code === form.status)?.requires_power_of_attorney ?? false,
    [statuses, form.status],
  );

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    setOk(null);
    setErr(null);
    try {
      // Пустые строки в null: бэк отличает «не заполнено» от пустой строки.
      const body: Record<string, unknown> = {};
      Object.entries(form).forEach(([k, v]) => {
        body[k] = v.trim() === '' ? null : v.trim();
      });
      await upsert(body).unwrap();
      setOk('Карточка сохранена. Она применится к следующим выпускам накладных.');
      setForm({ ...EMPTY, status: statuses[0]?.code ?? '' });
    } catch (e) {
      setErr(apiErrorText(e, 'Не удалось сохранить карточку'));
    }
  };

  const toggle = async (signer: LogistSigner) => {
    setOk(null);
    setErr(null);
    try {
      await setActive({ id: signer.id, active: !signer.is_active }).unwrap();
    } catch (e) {
      setErr(apiErrorText(e, 'Не удалось изменить карточку'));
    }
  };

  return (
    <Wrap>
      <Back to="/agents/logist">
        <IconArrowLeft size={14} /> К агенту
      </Back>
      <Title>Подписанты накладных</Title>
      <Subtitle>
        В электронную накладную входят ФИО подписанта, его должность и основание полномочий — по
        уставу или по доверенности. Заполните карточку на себя: при выпуске накладной она
        подставляется автоматически. Саму подпись вы по-прежнему ставите в кабинете оператора.
      </Subtitle>

      {canWrite && (
        <Card>
          <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>Моя карточка</h2>
          <Grid>
            <Field>
              <Label>ФИО полностью, как в доверенности *</Label>
              <Input
                value={form.full_name}
                onChange={(e) => set('full_name', e.target.value)}
                placeholder="Иванов Иван Иванович"
              />
            </Field>
            <Field>
              <Label>Должность</Label>
              <Input
                value={form.job_title}
                onChange={(e) => set('job_title', e.target.value)}
                placeholder="Начальник транспортного цеха"
              />
            </Field>
            <Field>
              <Label>Основание полномочий *</Label>
              <Select value={form.status} onChange={(e) => set('status', e.target.value)}>
                {statuses.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </Field>
          </Grid>

          {needsPoa && (
            <>
              <Note $kind="muted">
                Для полномочий по доверенности в накладную идут её идентификатор и дата. У
                машиночитаемой доверенности идентификатор — это её номер вида GUID из карточки
                доверенности.
              </Note>
              <Grid style={{ marginTop: 14 }}>
                <Field>
                  <Label>Идентификатор доверенности *</Label>
                  <Input
                    value={form.poa_id}
                    onChange={(e) => set('poa_id', e.target.value)}
                    placeholder="7f3c…-…-…"
                  />
                </Field>
                <Field>
                  <Label>Дата доверенности *</Label>
                  <Input
                    type="date"
                    value={form.poa_date}
                    onChange={(e) => set('poa_date', e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Номер доверенности</Label>
                  <Input value={form.poa_number} onChange={(e) => set('poa_number', e.target.value)} />
                </Field>
                <Field>
                  <Label>Действует до</Label>
                  <Input
                    type="date"
                    value={form.poa_valid_until}
                    onChange={(e) => set('poa_valid_until', e.target.value)}
                  />
                </Field>
              </Grid>
            </>
          )}

          <Row>
            <Button onClick={submit} disabled={saving || !form.full_name.trim()}>
              {saving ? 'Сохранение…' : 'Сохранить карточку'}
            </Button>
          </Row>
          {ok && <Note $kind="ok">{ok}</Note>}
          {err && <Note $kind="err">{err}</Note>}
        </Card>
      )}

      <Card>
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>Карточки сотрудников</h2>
        {isLoading ? (
          <Note $kind="muted">Загрузка…</Note>
        ) : signers.length === 0 ? (
          <Note $kind="muted">
            Карточек пока нет. Пока карточка не заполнена, выпустить накладную нельзя — формат
            требует сведения о подписанте.
          </Note>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>ФИО</Th>
                <Th>Должность</Th>
                <Th>Основание</Th>
                <Th>Доверенность</Th>
                <Th>Состояние</Th>
                {canWrite && <Th />}
              </tr>
            </thead>
            <tbody>
              {signers.map((s) => {
                const expired = isExpired(s);
                const label = statuses.find((x) => x.code === s.status)?.label ?? s.status;
                return (
                  <tr key={s.id}>
                    <Td>{s.full_name}</Td>
                    <Td>{s.job_title ?? '—'}</Td>
                    <Td>{label}</Td>
                    <Td>
                      {s.poa_id ? (
                        <>
                          {s.poa_number ? `№ ${s.poa_number}, ` : ''}
                          {s.poa_date ?? '—'}
                          {s.poa_valid_until ? ` — до ${s.poa_valid_until}` : ''}
                        </>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>
                      {!s.is_active ? (
                        <Badge $tone="off">отключена</Badge>
                      ) : expired ? (
                        <Badge $tone="warn">доверенность истекла</Badge>
                      ) : (
                        <Badge $tone="ok">действует</Badge>
                      )}
                    </Td>
                    {canWrite && (
                      <Td>
                        <Button $variant="secondary" onClick={() => toggle(s)}>
                          {s.is_active ? 'Отключить' : 'Включить'}
                        </Button>
                      </Td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </Wrap>
  );
}
